import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { api } from "@/helper/api";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import CompanySetupIncompleteModal from "@/components/company/companySetupIncompleteModal";
import { setSubjectSearch } from "@/components/state/subjectSearch/subjectSearchSlice";
import { ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED } from "@/helper/anchorDataCategories";

export interface CompanySetupTarget {
  id: string;
  name: string;
  subject_id?: string | null;
  subject?: { id: string } | null;
}

const getMissingCompanySetupFields = (error: unknown): string[] | null => {
  if (typeof error !== "object" || error === null || Array.isArray(error)) {
    return null;
  }
  const draftModeError = (error as Record<string, unknown>).draft_mode;
  if (
    typeof draftModeError !== "object" ||
    draftModeError === null ||
    Array.isArray(draftModeError)
  ) {
    return null;
  }
  const { code, fields } = draftModeError as Record<string, unknown>;
  if (
    code !== "company_mandatory_anchor_data_missing" ||
    !Array.isArray(fields)
  ) {
    return null;
  }
  const missingFields = Array.from(
    new Set(
      fields
        .filter((field): field is string => typeof field === "string")
        .map((field) => field.trim())
        .filter(Boolean),
    ),
  );
  return missingFields.length > 0 ? missingFields : null;
};

interface Options {
  onSuccess: () => void;
  onError: (error: unknown) => void;
  onRejected?: () => void;
}

export function useCompanySetupCompletion({
  onSuccess,
  onError,
  onRejected,
}: Options) {
  const { t } = useTranslation();
  const { showConfirmationModalAsync } = useConfirmationModal();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inFlight = useRef(false);
  const [pendingCompanyId, setPendingCompanyId] = useState<string | null>(null);
  const [incomplete, setIncomplete] = useState<{
    company: CompanySetupTarget;
    fields: string[];
  } | null>(null);

  const openAnchorData = (company: CompanySetupTarget) => {
    const parameters = new URLSearchParams({
      company: company.id,
      category: ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED,
    });
    window.open(`/admin/anchorData?${parameters}`, "_blank", "noopener,noreferrer");
  };

  const navigateToMissingAnchorData = (company: CompanySetupTarget) => {
    const subjectId = company.subject?.id || company.subject_id;
    dispatch(
      subjectId
        ? setSubjectSearch({
            value: subjectId,
            label: company.name,
            companyId: company.id,
            is_company: true,
          })
        : { type: "clearSubjectSearch" },
    );
    navigate(
      `/admin/anchorData?category=${ANCHORDATA_VIRTUAL_CATEGORY_MISSING_REQUIRED}`,
    );
  };

  const completeSetup = async (company: CompanySetupTarget) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setPendingCompanyId(company.id);
    const finish = () => {
      inFlight.current = false;
      setPendingCompanyId(null);
    };
    const fail = (error: unknown) => {
      finish();
      const fields = getMissingCompanySetupFields(error);
      if (fields) setIncomplete({ company, fields });
      else onError(error);
      onRejected?.();
    };
    try {
      const confirmed = await showConfirmationModalAsync(
        t("company_setup_confirmation", { name: company.name }),
        { disableConfirmFor: 3000 },
      );
      if (!confirmed) {
        finish();
        return;
      }
      api.genericApiRequest({
        method: "put",
        entity: "company",
        entityId: company.id,
        submitData: { draft_mode: false },
        successHandler: () => {
          finish();
          onSuccess();
        },
        failHandler: fail,
      });
    } catch (error) {
      fail(error);
    }
  };

  const setupIncompleteModal = (
    <CompanySetupIncompleteModal
      open={incomplete !== null}
      companyName={incomplete?.company.name ?? ""}
      missingFields={incomplete?.fields ?? []}
      onClose={() => setIncomplete(null)}
      onEditAnchorData={() => {
        if (incomplete) navigateToMissingAnchorData(incomplete.company);
        setIncomplete(null);
      }}
    />
  );
  return {
    completeSetup,
    openAnchorData,
    pendingCompanyId,
    setupIncompleteModal,
  };
}
